import {Injectable} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {auditTime, first, takeUntil} from 'rxjs/operators';
import {Field} from '../model/field';
import {Condition} from '../model/condition';
import {IndexTrackerService} from './index-tracker.service';
import {AvisDataLoaderService} from './avis-data-loader.service';
import {EformFlatFieldsService} from './eform-flat-fields.service';
import {EformDuplicableFieldsService} from './eform-duplicable-fields.service';
import {EformConditionEvaluatorService} from './eform-condition-evaluator.service';
import {EformFieldCleanupService} from './eform-field-cleanup.service';
import {EformDuplicableEntityService} from './eform-duplicable-entity.service';

/**
 * Service de gestion des conditions et de la visibilité des champs dans les formulaires eForm.
 *
 * Ce service est une façade qui centralise et délègue vers les services spécialisés :
 * - EformFlatFieldsService : gestion des flatFields (scan, update, reset)
 * - EformDuplicableFieldsService : gestion des éléments duplicables (add, remove, visibility)
 * - EformConditionEvaluatorService : évaluation des conditions (simple, group, subCondition)
 * - EformFieldCleanupService : nettoyage des champs interdits
 *
 * @version 2.0
 * @since Angular 12+
 * @author Équipe AIFE
 */
@Injectable({
  providedIn: 'root'
})
export class EformConditionsV2Service {
  /** Subject pour gérer le désabonnement propre des observables */
  private destroy$ = new Subject<void>();

  /** Timestamp du dernier rescan pour la détection de cycles */
  private lastRescanTime = 0;

  /** Compteur de rescans rapprochés pour la détection de cycles infinis */
  private rescanCount = 0;

  /** Flag pour bloquer temporairement les rescans en cas de cycle détecté */
  private isRescanBlocked = false;

  /** Référence au FormGroup courant */
  private currentFormGroup: FormGroup | null = null;

  /**
   * Constructeur du service EformConditionsV2Service.
   */
  constructor(
    private indexTrackerService: IndexTrackerService,
    private avisDataLoaderService: AvisDataLoaderService,
    private flatFieldsService: EformFlatFieldsService,
    private duplicableFieldsService: EformDuplicableFieldsService,
    private conditionEvaluatorService: EformConditionEvaluatorService,
    private fieldCleanupService: EformFieldCleanupService,
    private eformDuplicableEntityService: EformDuplicableEntityService
  ) {
    this.initializeServices();
  }

  /**
   * Initialise les connexions entre les services
   */
  private initializeServices(): void {
    // Configure l'accès aux flatFields pour le service d'évaluation
    this.conditionEvaluatorService.setFlatFieldsProvider(() => this.flatFieldsService.flatFieldsValue);

    // 🔧 FIX 2026-02-23: Configure l'accès à l'index réel affiché pour l'évaluation des conditions
    // Cela permet de filtrer correctement les valeurs des champs duplicables (LOT/PAR)
    this.conditionEvaluatorService.setDisplayedRealIndexProvider(
      (type) => this.flatFieldsService.getDisplayedRealIndex(type)
    );

    // ✅ FIX 2025-12-23: Configure le fallback pour displayedElementIndex
    // Cela garantit que l'index est toujours correct lors du changement d'accordéon
    this.flatFieldsService.setDisplayedElementIndexFallback(
      (type) => this.conditionEvaluatorService.getDisplayedElementIndex(type)
    );

    // Configure l'accès aux flatFields pour le service des champs duplicables
    this.duplicableFieldsService.setFlatFieldsAccessors(
      () => this.flatFieldsService.flatFieldsValue,
      (value) => this.flatFieldsService.setFlatFields(value)
    );

    // Configure l'accès aux flatFields et l'évaluateur pour le service de nettoyage
    this.fieldCleanupService.setFlatFieldsAccessors(
      () => this.flatFieldsService.flatFieldsValue,
      (value) => this.flatFieldsService.setFlatFields(value),
      (group, parentPath, map) => this.flatFieldsService.scanFormGroupToFlatFields(group, parentPath, map)
    );
    this.fieldCleanupService.setConditionEvaluator(
      (condition, elementId, path) => this.evaluateConditions(condition, elementId, path)
    );

    // 🔧 FIX 2026-01-22: S'abonner à avisWithElementsDuplicable pour scanner automatiquement
    // les données de tous les lots/éléments duplicables quand elles arrivent.
    // Cela résout le problème de timing lors de la création via API de redirection.
    this.eformDuplicableEntityService.avisWithElementsDuplicable
      .pipe(takeUntil(this.destroy$))
      .subscribe(avisData => {
        if (avisData) {
          this.flatFieldsService.scanJsonToFlatFields(avisData);
          this.flatFieldsService.eformFlatFieldsReady = true;

          // ✅ FIX 2026-02-19: Attendre que le formulaire soit prêt avant de lancer le cleanup
          // Au lieu d'un délai arbitraire, on attend le signal isFormReady
          // 🔧 FIX 2026-04-22: Utiliser first(ready => ready) + take(1) pour éviter
          // l'accumulation de subscriptions internes à chaque mise à jour de avisWithElementsDuplicable.
          // L'ancienne version créait un nouveau subscribe à isFormReady sans jamais l'annuler,
          // causant des appels multiples à cleanupForbiddenFieldsWithConditions après chaque save.
          if (this.currentFormGroup) {
            this.eformDuplicableEntityService.isFormReady
              .pipe(
                first(ready => ready === true),
                takeUntil(this.destroy$)
              )
              .subscribe(() => {
                this.fieldCleanupService.cleanupForbiddenFieldsWithConditions(this.currentFormGroup!);
              });
          }
        }
      });
  }

  // ==================== PROPRIÉTÉS PUBLIQUES DÉLÉGUÉES ====================

  /** Flag indiquant si les flatFields ont été initialisés et sont prêts à être utilisés */
  get eformFlatFieldsReady(): boolean {
    return this.flatFieldsService.eformFlatFieldsReady;
  }

  set eformFlatFieldsReady(value: boolean) {
    this.flatFieldsService.eformFlatFieldsReady = value;
  }

  /** Subject pour notifier les composants qu'une mise à jour de flatFields a eu lieu après une suppression */
  get flatFieldsUpdatedAfterDeletion$(): Subject<{ type: string, index: number }> {
    return this.flatFieldsService.flatFieldsUpdatedAfterDeletion$;
  }

  // ==================== MÉTHODES PUBLIQUES DÉLÉGUÉES - FLAT FIELDS ====================

  /**
   * Réinitialise la map flatFields.
   */
  resetFlatFields(): void {
    this.flatFieldsService.resetFlatFields();
  }

  /**
   * Force un rescan de flatFields après la suppression d'un élément duplicable.
   */
  forceRescanAfterDeletion(formGroup: FormGroup, type: string, index: number): void {
    this.flatFieldsService.forceRescanAfterDeletion(formGroup, type, index);
  }

  /**
   * Modifie un path pour remplacer l'index après le chemin de l'enum par l'index fourni
   */
  updatePathIndex(path: string, enumPath: string, newIndex: number): string {
    return this.flatFieldsService.updatePathIndex(path, enumPath, newIndex);
  }

  /**
   * Définit l'index réel affiché pour un type d'élément duplicable.
   * @param type Le type d'élément duplicable (ORG, CON, TEN, etc.)
   * @param realIndex L'index réel de l'élément dans la collection complète
   */
  setDisplayedRealIndex(type: string, realIndex: number): void {
    this.flatFieldsService.setDisplayedRealIndex(type, realIndex);
  }

  /**
   * Récupère l'index réel affiché pour un type d'élément duplicable.
   * @param type Le type d'élément duplicable
   * @returns L'index réel ou undefined si non défini
   */
  getDisplayedRealIndex(type: string): number | undefined {
    return this.flatFieldsService.getDisplayedRealIndex(type);
  }

  /**
   * Met à jour directement la valeur d'un champ dans flatFields par son inputId et contexte duplicable.
   * Cette méthode doit être appelée à chaque modification de valeur pour garder flatFields synchronisé.
   *
   * @param inputId L'identifiant du champ (ex: OPT-321-Tender)
   * @param value La nouvelle valeur
   * @param duplicableType Le type d'élément duplicable (TEN, RES, ORG, etc.) ou undefined si non duplicable
   * @param duplicableIndex L'index de l'élément duplicable ou undefined si non duplicable
   */
  updateFlatFieldValue(inputId: string, value: any, duplicableType?: string, duplicableIndex?: number): void {
    this.flatFieldsService.updateFlatFieldValue(inputId, value, duplicableType, duplicableIndex);
  }

  /**
   * Extrait le type et l'index d'un objet duplicable à partir de son path et de son id.
   * @param path Le chemin du champ (ex: tender[]0.id)
   * @param id L'identifiant du champ (ex: OPT-321-Tender)
   * @returns Un objet avec le type (ex: TEN) et l'index (ex: 0) ou null si non duplicable
   */
  extractDuplicableInfo(path: string, id: string): { type: string | null, index: number | null } {
    return this.flatFieldsService.extractDuplicableInfo(path, id);
  }

  // ==================== MÉTHODES PUBLIQUES DÉLÉGUÉES - DUPLICABLE FIELDS ====================

  /**
   * Duplique dynamiquement un groupe de champs marqués comme duplicables.
   */
  addDuplicableFieldsByType(duplicableType: string): void {
    this.duplicableFieldsService.addDuplicableFieldsByType(duplicableType);
  }

  /**
   * Supprime un champ duplicable spécifique en fonction de son type et de son index.
   */
  removeDuplicableFieldByTypeAndIndex(duplicableType: string, indexToRemove: number): void {
    this.duplicableFieldsService.removeDuplicableFieldByTypeAndIndex(duplicableType, indexToRemove);
  }

  /**
   * Met à jour la visibilité des champs pour un type d'objet duplicable donné.
   */
  updateDuplicableFieldsVisibility(type: string, newIndex: number): void {
    this.duplicableFieldsService.updateDuplicableFieldsVisibility(type, newIndex);
  }

  /**
   * Modifie l'état de rechargement d'un élément duplicable avec tracking par TYPE.
   */
  setReloadingDuplicableElement(isReloading: boolean, elementType: string = 'UNKNOWN', formGroup?: FormGroup): void {
    this.duplicableFieldsService.setReloadingDuplicableElement(isReloading, elementType);
  }

  /**
   * Observable qui émet lorsque l'état de rechargement d'un élément duplicable change.
   */
  get isReloadingDuplicableElement$(): Observable<boolean> {
    return this.duplicableFieldsService.isReloadingDuplicableElement$;
  }

  /**
   * Retourne la valeur actuelle de isReloadingDuplicableElement
   */
  get isReloadingDuplicableElementValue(): boolean {
    return this.duplicableFieldsService.isReloadingDuplicableElementValue;
  }

  /**
   * Active ou désactive le mode de chargement des préférences.
   * Lorsque actif, les conditions de visibilité sont désactivées.
   */
  setLoadingPreference(isLoading: boolean): void {
    this.duplicableFieldsService.setLoadingPreference(isLoading);
  }

  /**
   * Retourne la valeur actuelle de isLoadingPreference
   */
  get isLoadingPreferenceValue(): boolean {
    return this.duplicableFieldsService.isLoadingPreferenceValue;
  }

  /**
   * Active ou désactive le mode de chargement d'un avis lié.
   * Lorsque actif, les conditions de visibilité sont désactivées pour permettre
   * le remplissage des champs conditionnels lors de la création d'un avis lié.
   */
  setLoadingAvisLie(isLoading: boolean): void {
    this.duplicableFieldsService.setLoadingAvisLie(isLoading);
  }

  /**
   * Retourne la valeur actuelle de isLoadingAvisLie
   */
  get isLoadingAvisLieValue(): boolean {
    return this.duplicableFieldsService.isLoadingAvisLieValue;
  }

  /**
   * ✅ FIX 2026-03-05: Active ou désactive le mode de chargement d'un LOT.
   * Lorsque actif, les conditions de visibilité sont désactivées UNIQUEMENT pour les champs des LOT
   * pour permettre le remplissage des champs conditionnels (comme BT-5421-Lot).
   */
  setLoadingLot(isLoading: boolean): void {
    this.duplicableFieldsService.setLoadingLot(isLoading);
  }

  /**
   * Retourne la valeur actuelle de isLoadingLot
   */
  get isLoadingLotValue(): boolean {
    return this.duplicableFieldsService.isLoadingLotValue;
  }

  /**
   * Observable public qui s'émet quand TOUS les rechargements d'éléments duplicables sont terminés
   */
  get allReloadingComplete(): Observable<void> {
    return this.duplicableFieldsService.allReloadingComplete;
  }

  /**
   * Indique si le nettoyage post-rechargement d'un élément duplicable est en cours
   */
  isCleaningUpAfterReloadInProgress(): boolean {
    return this.duplicableFieldsService.isCleaningUpAfterReloadInProgress();
  }

  /**
   * Observable pour être notifié quand le nettoyage post-rechargement commence/termine
   */
  get isCleaningUpAfterReload$(): Observable<boolean> {
    return this.duplicableFieldsService.isCleaningUpAfterReload$;
  }

  /**
   * Déclenche le nettoyage post-rechargement
   */
  triggerCleanupAfterReload(formGroup?: FormGroup): void {
    this.duplicableFieldsService.triggerCleanupAfterReload();
  }

  /**
   * Réinitialise tous les flags de conditions pour éviter qu'ils ne restent bloqués.
   * À appeler lors de la destruction du composant eforms-generator.
   */
  resetAllConditionFlags(): void {
    this.duplicableFieldsService.resetAllFlags();
  }

  // ==================== MÉTHODES PUBLIQUES DÉLÉGUÉES - CONDITION EVALUATOR ====================

  /**
   * Récupère les valeurs d'un champ par son inputId.
   */
  getFieldValue(inputId: string): any | any[] {
    return this.conditionEvaluatorService.getFieldValue(inputId);
  }

  /**
   * Récupère la valeur d'un champ dans le contexte d'un élément duplicable donné.
   * Sans repeatablePath, un champ présent en plusieurs exemplaires (organisations,
   * lots, parts…) renvoie les valeurs de toutes ses occurrences.
   */
  getFieldValueForPath(inputId: string, repeatablePathForDuplicableElement?: string): any | any[] {
    return this.conditionEvaluatorService.getFieldValueForPath(inputId, repeatablePathForDuplicableElement);
  }

  /**
   * Compte le nombre d'occurrences d'un champ avec un inputId donné.
   */
  getFieldOccurrenceCount(inputId: string): number {
    return this.conditionEvaluatorService.getFieldOccurrenceCount(inputId);
  }

  /**
   * Recherche une section duplicable dans un identifiant.
   */
  getDuplicableSectionFromId(id: string): string | undefined {
    return this.conditionEvaluatorService.getDuplicableSectionFromId(id);
  }

  /**
   * Vérifie si une valeur est un champ (inputId).
   */
  isField(value: any): boolean {
    return this.conditionEvaluatorService.isField(value);
  }

  /**
   * Parse une chaîne représentant un tableau en un tableau de chaînes.
   */
  parseArrayString(str: string): string[] {
    return this.conditionEvaluatorService.parseArrayString(str);
  }

  // ==================== MÉTHODES PUBLIQUES DÉLÉGUÉES - FIELD CLEANUP ====================

  /**
   * Définit le nom du formulaire courant pour charger ses métadonnées
   */
  setCurrentFormName(formName: string): void {
    this.fieldCleanupService.setCurrentFormName(formName);
  }

  /**
   * Retourne les éléments duplicables qui doivent être nettoyés avec info sur mandatory.
   */
  getCleanedDuplicableElements(): Array<{
    type: string,
    index: number,
    fieldsToClean: Map<string, { mandatory: boolean }>
  }> {
    return this.fieldCleanupService.getCleanedDuplicableElements();
  }

  /**
   * Enregistre l'évaluation de l'interdiction d'un identifiant de groupe répétable pour un élément donné.
   */
  setRepeatableGroupForbidden(type: string, index: number, forbidden: boolean): void {
    this.flatFieldsService.setRepeatableGroupForbiddenEvaluation(type, index, forbidden);
  }

  /**
   * Retourne l'évaluation d'interdiction enregistré pour un élément, ou undefined si jamais évalué.
   */
  getRepeatableGroupForbiddenVerdict(type: string, index: number): boolean | undefined {
    return this.flatFieldsService.getRepeatableGroupForbiddenEvaluation(type, index);
  }

  // ==================== MÉTHODES PUBLIQUES - LOGIQUE PRINCIPALE ====================

  /**
   * ✅ FIX 2026-03-05: Extrait le type d'élément duplicable depuis un field.id
   * @param fieldId ID du champ (ex: "BT-3202-Contract", "BT-5421-Lot")
   * @returns Le type d'élément ('LOT', 'CON', 'ORG', 'TEN', 'RES', 'TPA') ou null
   */
  /**
   * ✅ FIX 2026-03-05: Extraction du type d'élément duplicable depuis l'ID d'un champ
   * @param fieldId ID du champ (ex: "BT-3202-Contract", "BT-5421-Lot")
   * @returns Type d'élément (LOT, CON, ORG, TEN, RES, TPA) ou null
   */
  private getDuplicableElementTypeFromFieldId(fieldId: string): string | null {
    if (!fieldId) {
      return null;
    }

    // Types d'éléments duplicables et leurs suffixes dans les field.id
    const typeMap: { [key: string]: string } = {
      '-Lot': 'LOT',
      '-Contract': 'CON',
      '-Organization': 'ORG',
      '-Tenderer': 'TEN',
      '-Result': 'RES',
      '-TouchPointParty': 'TPA'
    };

    for (const [suffix, type] of Object.entries(typeMap)) {
      if (fieldId.includes(suffix)) {
        return type;
      }
    }

    return null;
  }

  /**
   * Vérifie si un champ doit être masqué selon ses conditions de visibilité.
   */
  isFieldForbidden(field: Field, elementId: string, repeatablePathForDuplicableElement: string): boolean {
    if (this.duplicableFieldsService.isReloadingDuplicableElementValue
        || this.duplicableFieldsService.isLoadingPreferenceValue
        || this.duplicableFieldsService.isLoadingAvisLieValue) {
      return false;
    }

    // ✅ FIX 2026-03-05: Protection générique pour TOUS les éléments duplicables
    const fieldType = this.getDuplicableElementTypeFromFieldId(field?.id);
    if (fieldType && this.duplicableFieldsService.isLoadingDuplicableElementOfType(fieldType)) {
      return false;
    }

    // ✅ LEGACY: Conserver isLoadingLot pour compatibilité (sera retiré plus tard)
    const isLotField = field?.id && field.id.includes('-Lot');
    if (isLotField && this.duplicableFieldsService.isLoadingLotValue) {
      return false;
    }

    if (!field?.forbiddenConditionV2) {
      return false;
    }

    let duplicableSectionId = this.getDuplicableSectionFromId(field.id);
    if (duplicableSectionId) {
      field.forbiddenConditionV2.duplicableSectionId = duplicableSectionId;
    }

    return this.evaluateConditions(field.forbiddenConditionV2, elementId, repeatablePathForDuplicableElement);
  }

  /**
   * Vérifie si un champ est obligatoire selon ses conditions de mandatoryConditionV2.
   */
  isFieldMandatory(field: Field, elementId: string, repeatablePathForDuplicableElement: string): boolean {
    // Désactiver les conditions pendant le rechargement, le chargement des préférences
    // OU pendant le chargement d'un avis lié (FIX 2026-01-23: évite la perte des champs conditionnés)
    if (this.duplicableFieldsService.isReloadingDuplicableElementValue
        || this.duplicableFieldsService.isLoadingPreferenceValue
        || this.duplicableFieldsService.isLoadingAvisLieValue) {
      return false;
    }

    // ✅ FIX 2026-03-05: Protection générique pour TOUS les éléments duplicables
    const fieldType = this.getDuplicableElementTypeFromFieldId(field?.id);
    if (fieldType && this.duplicableFieldsService.isLoadingDuplicableElementOfType(fieldType)) {
      return false;
    }

    // ✅ LEGACY: Conserver isLoadingLot pour compatibilité (sera retiré plus tard)
    const isLotField = field?.id && field.id.includes('-Lot');
    if (isLotField && this.duplicableFieldsService.isLoadingLotValue) {
      return false;
    }

    if (!field?.mandatoryConditionV2) {
      return false;
    }

    let duplicableSectionId = this.getDuplicableSectionFromId(field.id);
    if (duplicableSectionId) {
      field.mandatoryConditionV2.duplicableSectionId = duplicableSectionId;
    }

    return this.evaluateConditions(field.mandatoryConditionV2, elementId, repeatablePathForDuplicableElement);
  }

  /**
   * Initialise l'observation des changements sur un FormGroup et configure la détection
   * de cycles pour optimiser les performances.
   */
  initializeFormObservation(formGroup: FormGroup): void {
    this.currentFormGroup = formGroup;

    // Désabonner les observables précédents
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();

    // Scan initial
    this.loadDataFromBackend(formGroup);

    // Observer les changements d'index des éléments duplicables
    this.indexTrackerService.indexChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(change => {
        if (change) {
          const {type, newIndex} = change;
          this.updateDuplicableFieldsVisibility(type, newIndex);
        }
      });

    formGroup.valueChanges
      .pipe(
        auditTime(50),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // ✅ FIX 2025-12-23: Bloquer les rescans pendant le rechargement d'un élément duplicable
        // Cela évite les race conditions où le rescan utiliserait un displayedRealIndex obsolète
        if (this.isRescanBlocked || this.duplicableFieldsService.isReloadingDuplicableElementValue) {
          return;
        }

        const currentTime = Date.now();

        if (this.lastRescanTime > 0) {
          const timeDiff = currentTime - this.lastRescanTime;

          if (timeDiff < 100) {
            this.rescanCount++;
          } else {
            this.rescanCount = 0;
          }

          if (this.rescanCount >= 8) {
            this.isRescanBlocked = true;
            setTimeout(() => {
              this.isRescanBlocked = false;
              this.rescanCount = 0;
            }, 3000);
            return;
          }
        }

        this.lastRescanTime = currentTime;
        this.rescanFlatFields(formGroup);
      });
  }

  /**
   * Charge les données d'un avis complet depuis le backend et met à jour flatFields.
   * Si l'avis n'est pas disponible depuis le backend (cas de création via redirection),
   * le scan sera effectué automatiquement via l'abonnement à avisWithElementsDuplicable
   * configuré dans initializeServices().
   */
  private loadDataFromBackend(formGroup: FormGroup): void {
    this.avisDataLoaderService.getCompleteAvisWithCurrentId().subscribe(
      (avis) => {
        if (avis) {
          this.flatFieldsService.scanJsonToFlatFields(avis);
          this.flatFieldsService.eformFlatFieldsReady = true;

          // ✅ FIX 2026-02-19: Attendre que le formulaire soit prêt avant le cleanup
          this.eformDuplicableEntityService.isFormReady
            .pipe(takeUntil(this.destroy$))
            .subscribe(ready => {
              if (ready) {
                this.fieldCleanupService.cleanupForbiddenFieldsWithConditions(formGroup);
              }
            });
        } else {
          // Cas de création via redirection : les données seront scannées automatiquement
          // via l'abonnement à avisWithElementsDuplicable dans initializeServices()

          // Vérifier si avisWithElementsDuplicable est déjà disponible
          const avisWithDuplicables = this.eformDuplicableEntityService.avisWithElementsDuplicable.getValue();
          if (avisWithDuplicables && !this.flatFieldsService.eformFlatFieldsReady) {
            this.flatFieldsService.scanJsonToFlatFields(avisWithDuplicables);
            this.flatFieldsService.eformFlatFieldsReady = true;
            // ✅ FIX 2026-02-19: Attendre que le formulaire soit prêt
            this.eformDuplicableEntityService.isFormReady
              .pipe(takeUntil(this.destroy$))
              .subscribe(ready => {
                if (ready) {
                  this.fieldCleanupService.cleanupForbiddenFieldsWithConditions(formGroup);
                }
              });
          }
          // Sinon, l'abonnement dans initializeServices() s'en chargera
        }
      },
      (error) => {
        const avisWithDuplicables = this.eformDuplicableEntityService.avisWithElementsDuplicable.getValue();
        if (avisWithDuplicables) {
          this.flatFieldsService.scanJsonToFlatFields(avisWithDuplicables);
          this.flatFieldsService.eformFlatFieldsReady = true;
          // ✅ FIX 2026-02-19: Attendre que le formulaire soit prêt
          this.eformDuplicableEntityService.isFormReady
            .pipe(takeUntil(this.destroy$))
            .subscribe(ready => {
              if (ready) {
                this.fieldCleanupService.cleanupForbiddenFieldsWithConditions(formGroup);
              }
            });
        } else {
          this.rescanFlatFields(formGroup);
        }
      }
    );
  }

  /**
   * Reconstruit la map flatFields à partir du formulaire courant.
   */
  private rescanFlatFields(formGroup: FormGroup): void {
    this.flatFieldsService.rescanFlatFields(formGroup, (fg) => {
      this.fieldCleanupService.cleanupForbiddenFieldsWithConditions(fg);
    });
  }

  /**
   * 🔧 FIX 2026-01-22: Méthode publique pour forcer un rescan des flatFields depuis le formulaire.
   * Utilisé après le chargement des données duplicables pour garantir que toutes les valeurs
   * des lots sont disponibles dans flatFields avant l'évaluation des conditions.
   */
  rescanFlatFieldsFromForm(formGroup: FormGroup): void {
    this.flatFieldsService.rescanFlatFields(formGroup, (fg) => {
      this.fieldCleanupService.cleanupForbiddenFieldsWithConditions(fg);
    });
  }

  /**
   * 🔧 FIX 2026-01-22: Scanne les données du sessionStorage pour les éléments duplicables
   * et les ajoute dans flatFields. Utilisé après putDuplicableEntitiesInLocalStorage.
   */
  scanSessionStorageToFlatFields(sessionStorageKeys: string[], basePath: string): void {
    this.flatFieldsService.scanSessionStorageToFlatFields(sessionStorageKeys, basePath);
  }

  /**
   * Évalue une condition selon son type.
   */
  evaluateConditions(condition: Condition, elementId: string, repeatablePathForDuplicableElement: string): boolean {
    return this.conditionEvaluatorService.evaluateConditions(condition, elementId, repeatablePathForDuplicableElement);
  }
}
